/**
 * @fileoverview This file contains the base Zod schemas for agent flow inputs.
 * It is isolated to prevent circular dependencies.
 *
 * Two bases mirror the primitive/composite split in `AppAgentDefinition`:
 *   - `BaseFlowInputSchema`           — primitives. Has `customPrompt`.
 *   - `CompositeBaseFlowInputSchema`  — composites. No `customPrompt`.
 *
 * They diverge because composites have no prompt of their own; their job is
 * to orchestrate children. Forcing a `customPrompt` field on composites
 * would either lie about the data shape or invite the leak fixed in
 * commit 7b38700 (composite parent's prompt overriding every child).
 */

import { z } from 'zod';

/**
 * Fields every flow input carries, regardless of agent kind.
 */
const FlowMetadataSchema = z.object({
  logId: z.string().describe("The ID of the log entry to update."),
  model: z.string().describe("The full model identifier (e.g., 'google-ai/gemini-3-flash-preview')."),
  /**
   * Project ID for fetching project-specific agent configuration.
   * When provided, child agents in composites can fetch their own model overrides.
   * Optional to maintain backwards compatibility.
   */
  projectId: z.string().optional().describe("Project ID for project-specific agent configuration."),
});

/**
 * Base input schema for PRIMITIVE agent flows.
 *
 * Primitives render a Handlebars prompt template, so `customPrompt` is
 * required — `buildFlowInput` always populates it via `resolveCustomPrompt`
 * (caller override → variant → defaultPrompt).
 */
export const BaseFlowInputSchema = FlowMetadataSchema.extend({
  customPrompt: z.string().describe("A user-defined Handlebars prompt template."),
});

/**
 * Base input schema for COMPOSITE agent flows.
 *
 * Composites don't render a prompt; they orchestrate child agents. They
 * therefore have no `customPrompt`. `buildFlowInput` deliberately strips
 * any inherited `customPrompt` from the input bag so the parent composite's
 * prompt cannot leak into its children.
 */
export const CompositeBaseFlowInputSchema = FlowMetadataSchema;
